'use client';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LogIn, LogOut, Settings } from 'lucide-react';
import { APP_CONFIGS, AppType } from '@/lib/platform';
import { getAppSubscription, usePlatformAuth } from '@/lib/platform-access';
import { performClientLogout } from '@/lib/session-client';
import { api } from '@/lib/api';

export default function RootPage() {
  const { hydrated, ready, token, user, syncing } = usePlatformAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { data: announcements } = useQuery({
    queryKey: ['public', 'announcements'],
    queryFn: () => api.get('/admin/announcements/public').then(response => response.data as { main?: string }),
    enabled: Boolean(token),
  });

  useEffect(() => {
    if (!ready) return;
    if (token) localStorage.removeItem('access_token');
  }, [ready, token]);

  if (!hydrated || (!ready && token)) {
    return (
      <main className="min-h-screen bg-gray-950 px-6 py-10 text-gray-100">
        <div className="mx-auto max-w-6xl space-y-8">
          <div className="space-y-2">
            <h1 className="text-4xl font-semibold">Trading Tools</h1>
            <p className="max-w-2xl text-gray-400">Loading your platform session.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[0, 1, 2].map(index => (
              <section key={index} className="rounded-3xl border border-gray-800 bg-gray-900/80 p-6">
                <div className="space-y-3">
                  <div className="h-7 w-40 animate-pulse rounded bg-gray-800" />
                  <div className="h-4 w-full animate-pulse rounded bg-gray-800" />
                  <div className="h-4 w-3/4 animate-pulse rounded bg-gray-800" />
                  <div className="mt-8 h-10 w-24 animate-pulse rounded bg-gray-800" />
                </div>
              </section>
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (!ready) return null;

  async function handleLogout() {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    await performClientLogout('/login');
  }

  type LauncherApp = {
    appType?: AppType;
    appName: string;
    accentColor: string;
    description: string;
    href: string;
    comingSoon: boolean;
  };

  // Section + app order mirrors the sidebar "Other Apps" list in layout/AppShell.tsx — keep the two
  // in step. Investing sits after the automated tools (owner, 2026-08-11).
  const appSections: { title: string; apps: LauncherApp[] }[] = [
    {
      title: 'Discretionary Trading Tools',
      apps: [
        {
          ...APP_CONFIGS.trade_scalper,
          appName: 'Trade Scalper',
          description: 'Lightning-fast discretionary trading for scalpers and intraday traders.',
          href: '/trade-scalper/dashboard',
          comingSoon: false,
        },
        {
          // comingSoon flips to false at the launch promote, in the SAME deploy that sets
          // TRADE_DISC_ENABLED in the target environment's own env file (dark-flag rule).
          ...APP_CONFIGS.trade_disc,
          appName: 'Trade Disc',
          description: 'Professional algo tool for one-click strike shifts and fast position adjustments.',
          href: '/trade-disc/dashboard',
          comingSoon: true,
        },
      ],
    },
    {
      title: 'Strategy Automation Tools',
      apps: [
        {
          ...APP_CONFIGS.trade_builder,
          appName: 'Trade Builder',
          description: 'Build indicator-based algo strategies from signals such as RSI, EMA, and MACD.',
          href: '/trade-builder/dashboard',
          comingSoon: false,
        },
        {
          ...APP_CONFIGS.trade_bots,
          appName: 'Trade Bots',
          description: 'Automate time-based straddle and strangle strategies with scheduled entries and exits.',
          href: '/trade-bots/dashboard',
          comingSoon: false,
        },
        {
          ...APP_CONFIGS.trade_bridge,
          appName: 'Trade Bridge',
          description: 'Convert TradingView, Python, and other external signals to trading strategies.',
          href: '/trade-bridge/dashboard',
          comingSoon: false,
        },
      ],
    },
    {
      title: 'Investing Tools',
      apps: [
        {
          ...APP_CONFIGS.equity_rebalancer,
          appName: 'Equity Rebalancer',
          description: 'Rebalance an equal-weighted stock basket in one click, with profits spread across every stock.',
          href: '/equity-rebalancer/dashboard',
          comingSoon: false,
        },
      ],
    },
  ];

  return (
    <main className="min-h-screen bg-gray-950 px-6 py-10 text-gray-100">
      <div className="mx-auto max-w-6xl space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-4xl font-semibold">Trading Tools</h1>
            <p className="max-w-2xl text-gray-400">One platform - multiple tools. India's best and fastest algo trading platform</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {token ? (
              <>
                <Link href="/settings" className="inline-flex items-center gap-2 rounded-full border border-gray-800 px-4 py-2 text-sm text-gray-300 hover:border-gray-700 hover:text-white">
                  <Settings size={15} />
                  Settings
                </Link>
                <button
                  type="button"
                  onClick={() => { void handleLogout(); }}
                  disabled={isLoggingOut}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-800 px-4 py-2 text-sm text-gray-300 hover:border-gray-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <LogOut size={15} />
                  {isLoggingOut ? 'Logging out...' : 'Logout'}
                </button>
              </>
            ) : (
              <Link href="/login" className="inline-flex items-center gap-2 rounded-full border border-gray-800 px-4 py-2 text-sm text-gray-300 hover:border-gray-700 hover:text-white">
                <LogIn size={15} />
                Login
              </Link>
            )}
          </div>
        </div>

        {announcements?.main ? (
          <div className="rounded-2xl border border-cyan-900/60 bg-cyan-950/30 px-5 py-4 text-sm text-cyan-100">
            {announcements.main}
          </div>
        ) : null}

        <div className="space-y-8">
          {appSections.map(section => (
            <section key={section.title} className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-[0.24em] text-gray-500">{section.title}</h2>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {section.apps.map(app => {
                  const subscription = app.appType ? getAppSubscription(user, app.appType) : undefined;
                  const status = app.comingSoon ? 'Coming Soon' : !token ? 'Preview' : subscription?.isActive ? 'Active' : 'Subscribe';
                  return (
                    <section key={app.appName} className="flex min-h-[220px] flex-col justify-between rounded-3xl border border-gray-800 bg-gray-900/80 p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-2">
                          <h3 className="text-2xl font-semibold">{app.appName}</h3>
                          <p className="text-sm leading-6 text-gray-400">{app.description}</p>
                          <p className="text-xs text-gray-500">{status}{subscription?.subscriptionEndDate ? ` until ${subscription.subscriptionEndDate}` : ''}</p>
                        </div>
                        <span className="mt-2 h-3 w-3 flex-none rounded-full" style={{ backgroundColor: app.accentColor }} />
                      </div>
                      <div className="mt-8">
                        {app.comingSoon ? (
                          <div className="inline-flex rounded-full border border-gray-700 px-3 py-1 text-xs uppercase tracking-[0.2em] text-gray-400">Coming Soon</div>
                        ) : (
                          <Link href={app.href} className="btn-primary inline-flex">{token ? 'Open' : 'Preview'}</Link>
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {appSections.every(section => section.apps.length === 0) ? (
          <div className="rounded-3xl border border-gray-800 bg-gray-900/80 p-6 text-sm text-gray-400">
            No applications are enabled for this account yet.
          </div>
        ) : null}

        {syncing ? (
          <div className="text-sm text-gray-500">Refreshing session details...</div>
        ) : null}
      </div>
    </main>
  );
}
